/**
 * ImageUpload — UEIM-01
 * Reusable image upload component for moodboard design elements.
 * Handles file validation, upload progress, and preview.
 */
import { useState, useRef, useCallback } from "react";
import { Upload, X, Loader2, Image, Check } from "lucide-react";
import { moodboardApi } from "@/services/api";

const C = {
  gold:    "#C9A15F",
  slate:   "#7A706A",
  divider: "rgba(201,161,95,0.15)",
  sand:    "#FAF6F0",
  ivory:   "#FBF7F2",
};

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB  = 10;

export default function ImageUpload({ moodboardId, elementId, existingImage, onUploaded }) {
  const [preview,    setPreview]    = useState(existingImage?.storage_path ? moodboardApi.imageUrl(existingImage.storage_path) : null);
  const [uploading,  setUploading]  = useState(false);
  const [progress,   setProgress]   = useState(0);
  const [error,      setError]      = useState(null);
  const [caption,    setCaption]    = useState(existingImage?.caption || "");
  const [altText,    setAltText]    = useState(existingImage?.alt_text || "");
  const [dragOver,   setDragOver]   = useState(false);
  const [done,       setDone]       = useState(false);
  const inputRef = useRef(null);

  const validate = (file) => {
    if (!ALLOWED.includes(file.type))
      return "Unsupported format. Please use JPEG, PNG, or WEBP.";
    if (file.size > MAX_MB * 1024 * 1024)
      return `File exceeds ${MAX_MB} MB limit.`;
    return null;
  };

  const upload = useCallback(async (file) => {
    const err = validate(file);
    if (err) { setError(err); return; }
    setError(null);
    setUploading(true);
    setProgress(0);
    setDone(false);
    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    try {
      const form = new FormData();
      form.append("file", file);
      // Simulate progress since axios doesn't expose upload progress on small files
      const interval = setInterval(() => setProgress(p => Math.min(p + 15, 85)), 120);
      const res = await moodboardApi.uploadImage(moodboardId, elementId, form, "primary", caption, altText);
      clearInterval(interval);
      setProgress(100);
      setDone(true);
      onUploaded && onUploaded(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Please try again.");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  }, [moodboardId, elementId, caption, altText, onUploaded]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = "";
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <div data-testid="image-upload-component">
      {/* Drop zone / preview */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          border: `2px dashed ${dragOver ? C.gold : C.divider}`,
          background: dragOver ? `${C.gold}10` : C.sand,
          transition: "border-color 0.2s, background 0.2s",
          minHeight: "140px",
          cursor: uploading ? "default" : "pointer",
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt="Reference preview"
              className="w-full h-40 object-cover"
              data-testid="image-preview"
            />
            {/* Overlay when not uploading */}
            {!uploading && (
              <div
                className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                style={{ background: "rgba(58,51,48,0.5)" }}
              >
                <p className="text-white text-xs font-medium">Click to replace</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 h-full py-8">
            {uploading ? (
              <Loader2 size={22} className="animate-spin" style={{ color: C.gold }} />
            ) : (
              <Upload size={22} style={{ color: C.slate, opacity: 0.5 }} />
            )}
            <p className="text-xs text-center" style={{ color: C.slate }}>
              {uploading ? "Uploading…" : "Drop image here or click to browse"}
            </p>
            <p className="text-[10px]" style={{ color: C.slate }}>JPEG · PNG · WEBP · max {MAX_MB}MB</p>
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: `${C.gold}30` }}>
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${progress}%`, background: C.gold }}
            />
          </div>
        )}

        {/* Done tick */}
        {done && !uploading && (
          <div
            className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: "#2E7D4F" }}
          >
            <Check size={12} color="#fff" />
          </div>
        )}
      </div>

      {/* Hidden input */}
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED.join(",")}
        className="hidden"
        onChange={onFileChange}
        data-testid="image-file-input"
      />

      {/* Caption + alt text */}
      <div className="mt-2.5 space-y-2">
        <input
          className="w-full text-xs border rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-yellow-400"
          style={{ borderColor: C.divider }}
          placeholder="Caption (optional)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          data-testid="image-caption-input"
        />
        <input
          className="w-full text-xs border rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-yellow-400"
          style={{ borderColor: C.divider }}
          placeholder="Alt text for accessibility"
          value={altText}
          onChange={e => setAltText(e.target.value)}
          data-testid="image-alt-input"
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs mt-1.5" style={{ color: "#C64040" }} data-testid="image-upload-error">
          {error}
        </p>
      )}
    </div>
  );
}
